'use client';

import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { contactPhoneCountry } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import codeIllustration from '@repo/ui/pages/settings-pages/security/assets/sms-setup-intro.svg';
import { formatResendClock } from '@repo/ui/pages/settings-pages/security/mfa-sms-setup-utils';

import { SmsCodeEntry } from './sms-code-fields';
import { formatMaskedUsPhone } from './us-phone-input';
import { useResendCountdown } from './use-resend-countdown';
import type { SmsMarketingSignupFlow } from './use-sms-marketing-signup-flow';

import './sms-verify-code-dialog.scss';

const ids = DataTestIds.SMS_MARKETING;

interface SmsVerifyCodeDialogProps {
  flow: SmsMarketingSignupFlow;
  /** The ref the flow focuses when a code is waiting or refused. */
  codeRef: RefObject<HTMLDivElement | null>;
  title: string;
  dismissLabel: string;
  maskedPhone: string;
  resendAvailableAt: Date;
  /** Offers a voice call next to resending the text; the main page's anonymous signup can only resend a text. */
  canCall?: boolean;
  /** Closes the dialog; the code stays valid, so saving again with the same number reopens it. */
  onDismiss: () => void;
  onVerify: () => void;
  onChangeNumber: () => void;
}

/** Takes the code sent to a number entered on onboarding or account settings. */
export function SmsVerifyCodeDialog({
  flow,
  codeRef,
  title,
  dismissLabel,
  maskedPhone,
  resendAvailableAt,
  canCall = true,
  onDismiss,
  onVerify,
  onChangeNumber,
}: SmsVerifyCodeDialogProps) {
  const { t } = useTranslation('common');
  const { busyAction, failure } = flow;
  const isBusy = busyAction !== undefined;
  const isVerifying = busyAction === 'verify';
  const isVoiceOnly = contactPhoneCountry(maskedPhone) === 'AE';

  return (
    <SDSDialog
      isOpen
      onClose={onDismiss}
      className="SmsVerifyCodeDialog"
      size="small"
      title={title}
      titleIcon={
        <div className="SmsVerifyCodeDialog__hero">
          <img alt="" className="SmsVerifyCodeDialog__heroImg" src={codeIllustration} />
        </div>
      }
      showCloseButton={false}
      dataTestId={ids.VERIFY_DIALOG}
      primaryAction={{
        label: t('smsMarketing.verify'),
        onClick: onVerify,
        loading: isVerifying,
        disabled: !isVerifying && (!flow.isCodeComplete || isBusy),
        dataTestId: ids.VERIFY_BUTTON,
      }}
      secondaryAction={{ label: dismissLabel, onClick: onDismiss, dataTestId: ids.VERIFY_DIALOG_DISMISS }}
    >
      <SDSFlexbox direction="column" gap="text-medium" fullWidth>
        {/*
          A form of its own, so Enter in the code field verifies. The dialog renders in a portal, but React still
          passes the submit up to the onboarding or profile form around it, so it stops here.
        */}
        <form
          noValidate
          onSubmit={event => {
            event.preventDefault();
            event.stopPropagation();
            if (flow.isCodeComplete && !isBusy) {
              onVerify();
            }
          }}
        >
          <SmsCodeEntry
            instruction={t(
              isVoiceOnly ? 'smsMarketing.verifyDialog.codeCalled' : 'smsMarketing.verifyDialog.codeSent',
              {
                phone: formatMaskedUsPhone(maskedPhone),
              },
            )}
            code={flow.code}
            codeRef={codeRef}
            maskedPhone={maskedPhone}
            isDisabled={isBusy}
            onChangeCode={flow.updateCode}
          />
        </form>
        <div className="SmsVerifyCodeDialog__meta">
          <ResendLink
            isBusy={isBusy}
            resendAvailableAt={resendAvailableAt}
            onResend={() => void flow.resend()}
            onCall={canCall && !isVoiceOnly ? () => void flow.resend('call') : undefined}
          />
          <SDSPrimitiveButton dataTestId={ids.CHANGE_NUMBER_BUTTON} isDisabled={isBusy} onClick={onChangeNumber}>
            <SDSText color="primary" type="body-small" text={t('smsMarketing.changeNumber')} />
          </SDSPrimitiveButton>
        </div>
        <div role="alert" data-testid={ids.ERROR_MESSAGE}>
          {failure && <SDSText type="body-small" color="alert" text={t(`smsMarketing.errors.${failure}`)} />}
        </div>
      </SDSFlexbox>
    </SDSDialog>
  );
}

function ResendLink({
  isBusy,
  resendAvailableAt,
  onResend,
  onCall,
}: {
  isBusy: boolean;
  resendAvailableAt: Date;
  onResend: () => void;
  onCall?: () => void;
}) {
  const { t } = useTranslation('common');
  const secondsLeft = useResendCountdown(resendAvailableAt);

  if (secondsLeft > 0) {
    return (
      <SDSText
        color="muted"
        type="body-small"
        text={t('smsMarketing.resendIn', { time: formatResendClock(secondsLeft) })}
        dataTestId={ids.RESEND_COUNTDOWN}
      />
    );
  }
  return (
    <>
      <SDSPrimitiveButton dataTestId={ids.RESEND_BUTTON} isDisabled={isBusy} onClick={onResend}>
        <SDSText color="primary" type="body-small" text={t('smsMarketing.resend')} />
      </SDSPrimitiveButton>
      {onCall && (
        <SDSPrimitiveButton
          dataTestId={ids.CALL_BUTTON}
          isDisabled={isBusy}
          onClick={onCall}
          ariaLabel={t('smsMarketing.callVerification')}
        >
          <SDSText color="primary" type="body-small" text={t('smsMarketing.callInstead')} />
        </SDSPrimitiveButton>
      )}
    </>
  );
}
